// Payment/subscription UI – transaction logging is part of the
// "Transactions" flow in ARCHITECTURE.md.  See that file for details.

import { useState, useEffect } from "react";
import "./BuyNow.css";

/* ── DATA ─────────────────────────────────────── */
const plansData = {
  cv: {
    title: "CV Search",
    subtitle: "Resdex",
    icon: "🔍",
    plans: [
      { price: "15,000", name: "VALUE", features: ["10 CVs per Year", "Access to Candidate Database", "+ GST Applicable"] },
      { price: "30,000", name: "MAXX", features: ["25 CVs per Year", "Priority CV Access", "+ GST Applicable"], highlight: true },
      { price: "50,000", name: "SUPER MAXX", features: ["50 CVs per Year", "Premium CV Access", "+ GST Applicable"] },
    ],
  },
  job: {
    title: "Job Posting",
    subtitle: "Reach top talent",
    icon: "📢",
    plans: [
      { price: "15,000", name: "VALUE", features: ["2 Unique Job Postings / Year", "Standard Visibility", "+ GST Applicable"] },
      { price: "30,000", name: "MAXX", features: ["4 Unique Job Postings / Year", "Better Candidate Reach", "+ GST Applicable"], highlight: true },
      { price: "50,000", name: "SUPER MAXX", features: ["8 Unique Job Postings / Year", "Premium Listing Visibility", "+ GST Applicable"] },
    ],
  },
  salary: {
    title: "Salary Tool",
    subtitle: "Market intelligence",
    icon: "📊",
    plans: [
      { price: "15,000", name: "VALUE", features: ["Basic Salary Insights", "Limited Access", "+ GST Applicable"] },
      { price: "30,000", name: "MAXX", features: ["Advanced Salary Insights", "Industry Comparison", "+ GST Applicable"], highlight: true },
      { price: "50,000", name: "SUPER MAXX", features: ["Complete Salary Analytics", "Premium Reports Access", "+ GST Applicable"] },
    ],
  },
  career: {
    title: "Online Interviews",
    subtitle: "Hire without borders",
    icon: "🎥",
    plans: [
      { price: "15,000", name: "VALUE", features: ["5 Online Interviews / Year", "Basic Interview Tools", "+ GST Applicable"] },
      { price: "30,000", name: "MAXX", features: ["8 Online Interviews / Year", "Advanced Interview Tools", "+ GST Applicable"], highlight: true },
      { price: "50,000", name: "SUPER MAXX", features: ["15 Online Interviews / Year", "Premium Interview Experience", "+ GST Applicable"] },
    ],
  },
};
const faqData = [
  {
    q: "Is GST included in the price?",
    a: "No, 18% GST is added at checkout on all plans. A GST invoice will be emailed to you."
  },
  {
    q: "Can I upgrade my plan later?",
    a: "Yes. You can upgrade at any time and pay only the difference for the remaining period."
  },
  {
    q: "What payment methods are accepted?",
    a: "UPI, Credit/Debit Cards (Visa, Mastercard, RuPay), and Net Banking from all major Indian banks."
  },
  {
    q: "Is there a refund policy?",
    a: "Plans are non-refundable once activated. Please contact support if you face any issues."
  },
  {
    q: "Do unused CVs or postings carry forward?",
    a: "No, unused credits expire at the end of the 12-month plan period."
  },
  {
    q: "Can I purchase multiple plan types?",
    a: "Absolutely! You can buy CV Search, Job Posting, Salary Tool, and Online Interviews independently."
  }
];
const TAB_KEYS = ["cv", "job", "salary", "career"];

const TRUST_ITEMS = [
  { icon: "🏢", label: "50,000+", sub: "Companies Trust Us" },
  { icon: "⭐", label: "4.8 / 5", sub: "Customer Rating" },
  { icon: "🔒", label: "Secure", sub: "PCI-DSS Payments" },
  { icon: "🎯", label: "₹0", sub: "Hidden Charges" },
];

/* ── CHECKOUT MODAL ──────────────────────────── */
export function CheckoutModal({ plan, category, onClose }) {
  const [step, setStep] = useState(1); // 1=details, 2=payment, 3=success
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", gstin: "" });
  const [payMode, setPayMode] = useState("upi");
  const [agreed, setAgreed] = useState(false);
  const [selectedTopup, setSelectedTopup] = useState(null);

  const basePrice = parseInt(plan.price.replace(",", ""), 10);
  const topupPrice = selectedTopup ? (selectedTopup.price || 0) : 0;
  const gst = Math.round((basePrice + topupPrice) * 0.18);
  const total = basePrice + topupPrice + gst;
  const fmt = n => "₹" + n.toLocaleString("en-IN");

  // Log transaction when payment is successful (step 3)
  useEffect(() => {
    if (step === 3) {
      logTransaction();
    }
  }, [step]);

  const logTransaction = async () => {
    try {
      const employerId = localStorage.getItem("employerId");
      if (!employerId) return;

      await fetch(" /api/activity/transaction", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employerId: parseInt(employerId),
          planType: category.title.toLowerCase().replace(" ", "_"),
          amount: total,
          topupType: selectedTopup ? selectedTopup.label : null,
          paymentStatus: "completed",
          paymentMethod: payMode,
          planName: plan.name,
          companyName: form.company,
          gstin: form.gstin || null
        })
      });
    } catch (error) {
      console.error("Error logging transaction:", error);
    }
  };

  const handleField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canProceed1 = form.name && form.email && form.phone && form.company;

  return (
    <div className="bn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bn-modal">

        {/* Close */}
        <button className="bn-modal__close" onClick={onClose}>✕</button>

        {/* Progress */}
        <div className="bn-progress">
          {["Details", "Payment", "Confirm"].map((s, i) => (
            <div key={s} className={`bn-progress__step${step > i + 1 ? " bn-progress__step--done" : step === i + 1 ? " bn-progress__step--active" : ""}`}>
              <div className="bn-progress__circle">{step > i + 1 ? "✓" : i + 1}</div>
              <span className="bn-progress__label">{s}</span>
              {i < 2 && <div className="bn-progress__line" />}
            </div>
          ))}
        </div>

        {/* Order summary strip */}
        <div className="bn-order-strip">
          <div className="bn-order-strip__left">
            <span className="bn-order-strip__cat">{category.icon} {category.title}</span>
            <span className="bn-order-strip__plan">{plan.name} Plan</span>
            {selectedTopup && (
              <span className="bn-order-strip__topup">+ {selectedTopup.label.split(' – ')[0].trim()}</span>
            )}
          </div>
          <div className="bn-order-strip__right">
            <div className="bn-order-strip__prices">
              <span className="bn-order-strip__base-price">{fmt(basePrice)}</span>
              {selectedTopup && (
                <span className="bn-order-strip__addon-price">+ {fmt(topupPrice)}</span>
              )}
            </div>
            <span className="bn-order-strip__gst">+GST {fmt(gst)} = {fmt(total)}</span>
          </div>
        </div>

        {/* ── STEP 1: Details ── */}
        {step === 1 && (
          <div className="bn-modal__body">
            <h3 className="bn-modal__section-title">Your Details</h3>
            <div className="bn-form-grid">
              {[
                { k: "name", label: "Full Name *", ph: "Rahul Sharma", type: "text" },
                { k: "email", label: "Work Email *", ph: "rahul@company.com", type: "email" },
                { k: "phone", label: "Phone Number *", ph: "+91 98765 43210", type: "tel" },
                { k: "company", label: "Company Name *", ph: "TechCorp India Pvt Ltd", type: "text" },
                { k: "gstin", label: "GSTIN (optional)", ph: "29AAACT4545F1ZI", type: "text" },
              ].map(({ k, label, ph, type }) => (
                <div className="bn-form-field" key={k}>
                  <label className="bn-form-field__label">{label}</label>
                  <input className="bn-form-field__input" type={type} placeholder={ph}
                    value={form[k]} onChange={e => handleField(k, e.target.value)} />
                </div>
              ))}
            </div>

            {/* Top-ups Section */}
            {plan.topups && plan.topups.length > 0 && (
              <div className="bn-topup-section">
                <h4 className="bn-topup-title">Add-on Services (Optional)</h4>
                <div className="bn-topup-cards">
                  {plan.topups.map((t, idx) => (
                    <button
                      key={idx}
                      className={`bn-topup-card${selectedTopup?.id === t.id ? " selected" : ""}`}
                      onClick={() => setSelectedTopup(selectedTopup?.id === t.id ? null : t)}
                    >
                      <div className="bn-topup-card__icon">✨</div>
                      <div className="bn-topup-card__label">{t.label.split(' – ')[0].trim()}</div>
                      <div className="bn-topup-card__price">₹{(t.price).toLocaleString("en-IN")}</div>
                      <div className={`bn-topup-card__check${selectedTopup?.id === t.id ? " active" : ""}`}>✓</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bn-modal__actions">
              <button className="bn-btn bn-btn--outline" onClick={onClose}>Cancel</button>
              <button className="bn-btn bn-btn--primary" onClick={() => setStep(2)} disabled={!canProceed1}>
                Continue to Payment →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Payment ── */}
        {step === 2 && (
          <div className="bn-modal__body">
            <h3 className="bn-modal__section-title">Choose Payment Method</h3>

            <div className="bn-pay-methods">
              {[
                { id: "upi", label: "UPI", icon: "⚡", desc: "Google Pay, PhonePe, Paytm" },
                { id: "card", label: "Credit / Debit Card", icon: "💳", desc: "Visa, Mastercard, RuPay" },
                { id: "net", label: "Net Banking", icon: "🏦", desc: "All major Indian banks" },
              ].map(m => (
                <div key={m.id}
                  className={`bn-pay-method${payMode === m.id ? " bn-pay-method--active" : ""}`}
                  onClick={() => setPayMode(m.id)}>
                  <span className="bn-pay-method__icon">{m.icon}</span>
                  <div>
                    <div className="bn-pay-method__label">{m.label}</div>
                    <div className="bn-pay-method__desc">{m.desc}</div>
                  </div>
                  <span className="bn-pay-method__radio">{payMode === m.id ? "●" : "○"}</span>
                </div>
              ))}
            </div>

            {/* Order summary */}
            <div className="bn-summary">
              <div className="bn-summary__title">Order Summary</div>
              <div className="bn-summary__row"><span>{category.title} — {plan.name}</span><span>{fmt(basePrice)}</span></div>
              {selectedTopup && (
                <div className="bn-summary__row"><span>{selectedTopup.label}</span><span>{fmt(topupPrice)}</span></div>
              )}
              <div className="bn-summary__row"><span>GST (18%)</span><span>{fmt(gst)}</span></div>
              <div className="bn-summary__divider" />
              <div className="bn-summary__row bn-summary__row--total"><span>Total Payable</span><span>{fmt(total)}</span></div>
            </div>

            <label className="bn-checkbox">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
              <span>I agree to TalentHive's <a href="#" className="bn-link">Terms &amp; Conditions</a> and <a href="#" className="bn-link">Refund Policy</a>.</span>
            </label>

            <div className="bn-modal__actions">
              <button className="bn-btn bn-btn--outline" onClick={() => setStep(1)}>← Back</button>
              <button className="bn-btn bn-btn--primary" onClick={() => setStep(3)} disabled={!agreed}>
                Pay {fmt(total)} →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Success ── */}
        {step === 3 && (
          <div className="bn-modal__body bn-modal__body--center">
            <div className="bn-success-icon">🎉</div>
            <h3 className="bn-success__title">Payment Successful!</h3>
            <p className="bn-success__sub">
              Your <strong>{plan.name}</strong> plan for <strong>{category.title}</strong> is now active.
              A confirmation has been sent to <strong>{form.email}</strong>.
            </p>
            <div className="bn-success-details">
              <div className="bn-success-details__row"><span>Plan</span><strong>{category.title} — {plan.name}</strong></div>
              {selectedTopup && (
                <div className="bn-success-details__row"><span>Top-up</span><strong>{selectedTopup.label}</strong></div>
              )}
              <div className="bn-success-details__row"><span>Amount Paid</span><strong>{fmt(total)}</strong></div>
              <div className="bn-success-details__row"><span>Company</span><strong>{form.company}</strong></div>
              <div className="bn-success-details__row"><span>Invoice</span><strong>TH-2026-{Math.floor(Math.random() * 90000) + 10000}</strong></div>
            </div>
            <button className="bn-btn bn-btn--primary bn-btn--wide" onClick={onClose}>Go to Dashboard →</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── PLAN CARD ───────────────────────────────── */
function PlanCard({ plan, category, onBuy }) {
  return (
    <div className={`bn-plan-card${plan.highlight ? " bn-plan-card--highlight" : ""}`}>
      {plan.highlight && <div className="bn-plan-card__badge">Most Popular</div>}
      <div className="bn-plan-card__name">{plan.name}</div>
      <div className="bn-plan-card__price-wrap">
        <span className="bn-plan-card__currency">₹</span>
        <span className="bn-plan-card__price">{plan.price}</span>
        <span className="bn-plan-card__per">/ year</span>
      </div>
      <ul className="bn-plan-card__features">
        {plan.features.map((f, i) => (
          <li key={i} className={`bn-plan-card__feature${f.startsWith("+") ? " bn-plan-card__feature--note" : ""}`}>
            {!f.startsWith("+") && <span className="bn-plan-card__check">✓</span>}
            {f}
          </li>
        ))}
      </ul>
      <button
        className={`bn-plan-card__btn${plan.highlight ? " bn-plan-card__btn--accent" : ""}`}
        onClick={() => onBuy(plan)}
      >
        Buy Now →
      </button>
    </div>
  );
}

/* ── MAIN PAGE ───────────────────────────────── */
export default function BuyNow() {
  const [activeTab, setActiveTab] = useState("cv");
  const [modal, setModal] = useState(null); // { plan, category }

  const current = plansData[activeTab];
const [openIndex,setOpenIndex] = useState(null);

  const toggleFAQ = (index)=>{

    setOpenIndex(openIndex === index ? null : index);

  };
  return (
    <div className="bn-page">

      {/* ── HERO ── */}
      <header className="bn-hero">
        <div className="bn-hero__inner">
          <div className="bn-hero__eyebrow">TalentHive for Employers</div>
          <h1 className="bn-hero__title">
            Hire Smarter.<br />
            <span className="bn-hero__title--accent">Pay Once. Grow Faster.</span>
          </h1>
          <p className="bn-hero__sub">
            Choose the plan that fits your hiring needs. No hidden charges. Cancel anytime.
          </p>
          <div className="bn-hero__trust">
            {TRUST_ITEMS.map(t => (
              <div className="bn-hero__trust-item" key={t.label}>
                <span className="bn-hero__trust-icon">{t.icon}</span>
                <span className="bn-hero__trust-value">{t.label}</span>
                <span className="bn-hero__trust-sub">{t.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ── TABS ── */}
      <div className="bn-tabs-wrap">
        <div className="bn-tabs">
          {TAB_KEYS.map(k => {
            const d = plansData[k];
            return (
              <button
                key={k}
                className={`bn-tab${activeTab === k ? " bn-tab--active" : ""}`}
                onClick={() => setActiveTab(k)}
              >
                <span className="bn-tab__icon">{d.icon}</span>
                <span className="bn-tab__label">{d.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── PLAN SECTION ── */}
      <main className="bn-main">
        <div className="bn-section-head">
          <span className="bn-section-head__icon">{current.icon}</span>
          <div>
            <h2 className="bn-section-head__title">{current.title}</h2>
            <p className="bn-section-head__sub">{current.subtitle}</p>
          </div>
        </div>

        <div className="bn-plans-grid">
          {current.plans.map((plan, i) => (
            <PlanCard
              key={i}
              plan={plan}
              category={current}
              onBuy={plan => setModal({ plan, category: current })}
            />
          ))}
        </div>

        {/* Compare note */}
        <div className="bn-compare-note">
          <span>🔄</span>
          All plans are valid for <strong>12 months</strong> from activation. GST will be added at checkout. Need a custom plan?
          <a href="#" className="bn-link"> Contact our sales team →</a>
        </div>
      </main>

      {/* ── WHY TALENTHIVE ── */}
      <section className="bn-why">
        <div className="bn-why__inner">
          <h2 className="bn-why__title">Why Choose <span className="bn-why__title--accent">TalentHive?</span></h2>
          <div className="bn-why__grid">
            {[
              { icon: "🚀", title: "Instant Activation", desc: "Your plan goes live the moment payment is confirmed — zero waiting time." },
              { icon: "🎯", title: "Targeted Reach", desc: "Access India's largest verified candidate database across all industries." },
              { icon: "📞", title: "Dedicated Support", desc: "A dedicated account manager is assigned to you from Day 1." },
              { icon: "📈", title: "Hiring Analytics", desc: "Real-time dashboards to track your hiring pipeline and ROI." },
              { icon: "🔒", title: "Data Privacy", desc: "All candidate data is GDPR-compliant and securely encrypted." },
              { icon: "💰", title: "Best Price Guarantee", desc: "Found a lower price? We will match it — guaranteed." },
            ].map(w => (
              <div className="bn-why__card" key={w.title}>
                <div className="bn-why__card-icon">{w.icon}</div>
                <h3 className="bn-why__card-title">{w.title}</h3>
                <p className="bn-why__card-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bn-faq">

        <div className="bn-faq__inner">

          <h2 className="bn-faq__title">
            Frequently Asked Questions
          </h2>

          <div className="bn-faq__grid">

            {faqData.map((item, index) => (

              <div
                key={index}
                className={`bn-faq__item ${openIndex === index ? "active" : ""}`}
              >

                <div
                  className="bn-faq__header"
                  onClick={() => toggleFAQ(index)}
                >

                  <h4>{item.q}</h4>

                  <span className="bn-faq__icon">
                    {openIndex === index ? "−" : "+"}
                  </span>

                </div>

                <div className="bn-faq__answer">

                  <p>{item.a}</p>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* ── CHECKOUT MODAL ── */}
      {modal && (
        <CheckoutModal
          plan={modal.plan}
          category={modal.category}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
